import { v4 as uuidv4 } from 'uuid';
import { inject, Injectable } from '@angular/core';
import { DexieDatabaseService } from './dexieIndexDB.service';
import { environment } from '../../environments/environment';
import { OfflineService } from './offline.service';
import { PineEconomicAnalysisReport } from '../models/PEAT Tool/pine-economic-analysis-report.model';
import { HttpClient } from '@angular/common/http';
import {
  Action,
  DatabaseSyncAction,
  EntityType
} from '../models/databaseSync.model';
import { ThinningAndHarvestYield } from '../models/PEAT Tool/thinning-and-harvest-yield.model';
import { ThinningAndHarvestAge } from '../models/PEAT Tool/thinning-and-harvest-age.model';
import { PineEconomicAnalysisToolAccuYieldRow } from '../models/PEAT Tool/pine-economic-analysis-tool-accu-yield-row.model';
import {
  catchError,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  switchMap,
  timer
} from 'rxjs';
import {
  AccuTaskResponse,
  CreateTaskRequest,
  CreateTaskResponse
} from '../models/PEAT Tool/accu-task-response.model';
import {
  PeatDefaultValuesModels,
  PeatDefaultValuesNode,
  PineEconomicAnalysisDefault,
  ValueRange
} from '../components/map/dialogs/pine-economic-analysis-tool/data-acces/peat-default-values.models';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class PineEconomicAnalysisToolService {
  private dexieService = inject(DexieDatabaseService);
  private connectionService = inject(OfflineService);
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  isGuest = false;

  api: string = `${environment.apiURL}PineEconomicAnalysisReport`;
  tasks: string = environment.taskURL;
  isOnline: boolean = true;

  constructor() {
    const connectionService = this.connectionService;

    connectionService.isOnline.subscribe((status) => (this.isOnline = status));
    this.auth.user.subscribe((u) => (this.isGuest = u.isGuest));
  }

  async getPropertyReport(id: string): Promise<PineEconomicAnalysisReport> {
    return this.dexieService.pineEconomicAnalysisReports
      .where('propertyID')
      .equals(id)
      .first();
  }

  async getPaddockReport(id: string): Promise<PineEconomicAnalysisReport> {
    return this.dexieService.pineEconomicAnalysisReports
      .where('paddockID')
      .equals(id)
      .first();
  }

  async getSiteCurveAndAges() {
    return await this.dexieService.siteCurveThinningAndHarvestAges.toArray();
  }

  async getThinningAndHarvestAgesBySiteCurve(
    siteCurveId: number
  ): Promise<ThinningAndHarvestAge> {
    return this.dexieService.siteCurveThinningAndHarvestAges
      .where('siteCurveId')
      .equals(siteCurveId)
      .first();
  }

  async getThinningAndHarvestYieldByKey(
    key: string
  ): Promise<ThinningAndHarvestYield> {
    return this.dexieService.siteCurveThinningAndHarvestYields
      .where('yieldKey')
      .equals(key)
      .first();
  }

  async getSiteCurveAndYields() {
    return await this.dexieService.siteCurveThinningAndHarvestYields.toArray();
  }

  async getPeatDefaults() {
    const rows = await this.dexieService.peatDefaults.toArray();
    return this._transformPeatDefaults(rows);
  }

  async saveReport(report: PineEconomicAnalysisReport) {
    await this.dexieService.pineEconomicAnalysisReports.add(report);

    if (!this.isGuest) {
      if (this.isOnline) {
        const typeUrl =
          report.paddockID == null ? '/property/add' : '/paddock/add';
        this.http
          .post<PineEconomicAnalysisReport>(`${this.api}${typeUrl}`, report)
          .subscribe({
            next: (res) => console.log('saved', res),
            error: (err) => console.error('save failed', err)
          });
      } else {
        this.dexieService.databaseSyncActions.add(
          new DatabaseSyncAction(
            EntityType.PineEconomicAnalysisReports,
            report.pineEconomicAnalysisReportId,
            Action.Insert
          )
        );
      }
    }
  }

  async updateReportACCU(
    report: PineEconomicAnalysisReport,
    accus: Array<PineEconomicAnalysisToolAccuYieldRow>
  ) {
    await this.dexieService.pineEconomicAnalysisReports.update(
      report.pineEconomicAnalysisReportId,
      report
    );

    if (!this.isGuest) {
      if (this.isOnline) {
        this.http
          .post<PineEconomicAnalysisReport>(`${this.api}/accu/add`, {
            pineEconomicAnalysisReportId: report.pineEconomicAnalysisReportId,
            pineEconomicAnalysisAccuYieldRows: accus
          })
          .subscribe({
            next: (res) => console.log('saved', res),
            error: (err) => console.error('save failed', err)
          });
      } else {
        this.dexieService.databaseSyncActions.add(
          new DatabaseSyncAction(
            EntityType.PineEconomicAnalysisReports,
            report.pineEconomicAnalysisReportId,
            Action.Update
          )
        );
      }
    }
  }

  async updateDefaultValue(peatDefault: PineEconomicAnalysisDefault) {
    await this.dexieService.peatDefaults.update(peatDefault.id, peatDefault);

    if (!this.isGuest) {
      if (this.isOnline) {
        this.http
          .put<PineEconomicAnalysisReport>(
            `${this.api}/defaults/update`,
            peatDefault
          )
          .subscribe({
            next: (res) => this.getPeatDefaults(),
            error: (err) => console.error('save failed', err)
          });
      } else {
        this.dexieService.databaseSyncActions.add(
          new DatabaseSyncAction(
            EntityType.PeatDefault,
            peatDefault.id.toString(),
            Action.Update,
            peatDefault.id
          )
        );
      }
    }
  }

  async getAccuData(coordinates: number[]): Promise<AccuTaskResponse> {
    const obj: CreateTaskRequest = {
      coords: [coordinates[1], coordinates[0]]
    };
    const task$ = this.http.post<CreateTaskResponse>(this.tasks, obj).pipe(
      switchMap((res: CreateTaskResponse) => {
        return timer(50_000).pipe(
          switchMap(() =>
            this.http.get<AccuTaskResponse>(`${this.tasks}/${res.task_id}`)
          )
        );
      })
    );

    return firstValueFrom(
      task$.pipe(
        catchError((error) => {
          console.error('Failed to create ACCU task:', error);
          const errorTask: AccuTaskResponse = {
            task_id: '',
            task_errors: [],
            task_status: 'FAILURE',
            task_result: []
          };
          return of(errorTask);
        })
      )
    );
  }

  processACCUData(
    response: AccuTaskResponse
  ): PineEconomicAnalysisToolAccuYieldRow[] {
    return [
      ...(response.task_result ?? []).map((result) => ({
        accuYield: result.plo_id,
        status: 'Completed',
        accuYieldAgeOfPlantation: result.yr25_credits?.length
          ? [0, ...result.yr25_credits]
          : []
      })),

      ...(response.task_errors ?? []).map((taskError) => ({
        accuYield: taskError.plo_id,
        status: `${taskError.stage}: ${taskError.error}`,
        accuYieldAgeOfPlantation: []
      }))
    ];
  }

  async getAccuFromFailedPlos(
    coordinates: number[],
    plos: string[]
  ): Promise<AccuTaskResponse> {
    const obj: CreateTaskRequest = {
      coords: [coordinates[1], coordinates[0]]
    };

    let delay = 50_000;

    if (!plos.includes('ALL')) {
      obj['plo_to_reproc'] = plos;
      delay = (plos.length + 10) * 1000;
    }

    const task$ = this.http.post<CreateTaskResponse>(this.tasks, obj).pipe(
      switchMap((res: CreateTaskResponse) => {
        return timer(delay).pipe(
          switchMap(() =>
            this.http.get<AccuTaskResponse>(`${this.tasks}/${res.task_id}`)
          )
        );
      })
    );

    return firstValueFrom(
      task$.pipe(
        catchError((error) => {
          console.error('Failed to create ACCU task:', error);
          const errorTask: AccuTaskResponse = {
            task_id: '',
            task_errors: [],
            task_status: 'FAILURE',
            task_result: []
          };
          return of(errorTask);
        })
      )
    );
  }

  getDummyObject(): PineEconomicAnalysisReport {
    const dummyObject: PineEconomicAnalysisReport = {
      pineEconomicAnalysisReportId: uuidv4(),
      propertyID: '',
      paddockID: '',
      siteQuality: 2,
      species: 'RAD',
      wellSuited: 0,
      suitable: 0,
      moderatelySuitable: 0,
      unsuitable: 0,
      bioregion: '',
      thinAge: 13,
      harvestAge: 25,
      area: 0,
      coordinates: [],
      pineEconomicAnalysisRows: [
        {
          product: 'Large Sawlogs',
          nearestMill: 'Bell Bay',
          distance: 90,
          pineEconomicAnalysisHarvestYieldRows: [
            {
              thinRegime: 'TH',
              yieldKey: 'RAD_TH_I',
              thinAge: 13,
              harvestAge: 25,
              harvestYieldAgeOfPlantation: [
                0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                0, 0, 0, 0, 143.0378966, 0, 0, 0, 0, 0
              ]
            },
            {
              thinRegime: 'UT',
              yieldKey: 'RAD_UT_I',
              thinAge: 13,
              harvestAge: 25,
              harvestYieldAgeOfPlantation: [
                0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                0, 0, 0, 0, 85.09667624, 0, 0, 0, 0, 0
              ]
            }
          ]
        },
        {
          product: 'Small Sawlogs',
          nearestMill: 'Branxholm',
          distance: 20,
          pineEconomicAnalysisHarvestYieldRows: [
            {
              thinRegime: 'TH',
              yieldKey: 'RAD_TH_I',
              thinAge: 13,
              harvestAge: 25,
              harvestYieldAgeOfPlantation: [
                0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 58.73557277, 0, 0, 0, 0,
                0, 0, 0, 0, 0, 0, 0, 222.0958613, 0, 0, 0, 0, 0
              ]
            },
            {
              thinRegime: 'UT',
              yieldKey: 'RAD_UT_I',
              thinAge: 13,
              harvestAge: 25,
              harvestYieldAgeOfPlantation: [
                0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                0, 0, 0, 0, 380.2835956, 0, 0, 0, 0, 0
              ]
            }
          ]
        },
        {
          product: 'Posts',
          nearestMill: 'Longford',
          distance: 70,
          pineEconomicAnalysisHarvestYieldRows: [
            {
              thinRegime: 'TH',
              yieldKey: 'RAD_TH_I',
              thinAge: 13,
              harvestAge: 25,
              harvestYieldAgeOfPlantation: [
                0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 19.85047938, 0, 0, 0, 0,
                0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
              ]
            },
            {
              thinRegime: 'UT',
              yieldKey: 'RAD_UT_I',
              thinAge: 13,
              harvestAge: 25,
              harvestYieldAgeOfPlantation: [
                0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                0, 0, 0, 0, 0, 0, 0, 0, 0, 0
              ]
            }
          ]
        },
        {
          product: 'Pulplogs',
          nearestMill: 'New Norfolk',
          distance: 230,
          pineEconomicAnalysisHarvestYieldRows: [
            {
              thinRegime: 'TH',
              yieldKey: 'RAD_TH_I',
              thinAge: 13,
              harvestAge: 25,
              harvestYieldAgeOfPlantation: [
                0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 46.49927565, 0, 0, 0, 0,
                0, 0, 0, 0, 0, 0, 0, 91.50932617, 0, 0, 0, 0, 0
              ]
            },
            {
              thinRegime: 'UT',
              yieldKey: 'RAD_UT_I',
              thinAge: 13,
              harvestAge: 25,
              harvestYieldAgeOfPlantation: [
                0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                0, 0, 0, 0, 204.1276952, 0, 0, 0, 0, 0
              ]
            }
          ]
        }
      ],
      pineEconomicAnalysisAccuYieldRows: this.getDummyAccu()
    };

    return dummyObject;
  }

  getDummyAccu(): PineEconomicAnalysisToolAccuYieldRow[] {
    return [
      {
        accuYield: 'RAD_TH13_CF25',
        status: 'Completed',
        accuYieldAgeOfPlantation: [
          0, 0.532242522, 7.879684479, 29.74885913, 48.77489215, 58.91192881,
          62.49336392, 62.32689519, 60.23824217, 57.26238941, 53.9580071,
          47.95739844, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
        ]
      },
      {
        accuYield: 'RAD_TH13_CF27',
        status: 'Completed',
        accuYieldAgeOfPlantation: [
          0, 0.638691027, 9.455621375, 35.69863095, 58.52987058, 70.69431457,
          74.9920367, 74.79227423, 72.28589061, 68.71486729, 64.74960852,
          57.54887812, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
        ]
      },
      {
        accuYield: 'RAD_TH13_CF30',
        status: 'Completed',
        accuYieldAgeOfPlantation: [
          0, 0.70256013, 10.40118351, 39.26849405, 64.38285764, 77.76374603,
          82.49124037, 82.27150166, 79.51447967, 75.58635402, 71.22456937,
          63.30376593, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
        ]
      },
      {
        accuYield: 'RAD_TH15_CF27',
        status: 'Completed',
        accuYieldAgeOfPlantation: [
          0, 0.585466775, 8.667652927, 32.72374504, 53.65238137, 64.80312169,
          68.74270031, 68.55958471, 66.26206639, 62.98862835, 59.35380781,
          52.75313828, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
        ]
      },
      {
        accuYield: 'RAD_TH15_CF30',
        status: 'Completed',
        accuYieldAgeOfPlantation: [
          0, 0.644013452, 9.53441822, 35.99611954, 59.0176195, 71.28343386,
          75.61697034, 75.41554319, 72.88827303, 69.28749118, 65.28918859,
          58.02845211, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
        ]
      },
      {
        accuYield: 'RAD_TH17_CF30',
        status: 'Completed',
        accuYieldAgeOfPlantation: [
          0, 0.691915279, 10.24358982, 38.67351687, 63.4073598, 76.58550745,
          81.24137309, 81.02496375, 78.30971482, 74.44110623, 70.14540923,
          62.34461797, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
        ]
      },
      {
        accuYield: 'RAD_UT_CF25',
        status: 'Completed',
        accuYieldAgeOfPlantation: [
          0, 0.798363784, 11.81952672, 44.62328869, 73.16233823, 88.36789322,
          93.74004587, 93.49034279, 90.35736326, 85.89358411, 80.93701065,
          71.93609765, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
        ]
      },
      {
        accuYield: 'RAD_UT_CF27',
        status: 'Completed',
        accuYieldAgeOfPlantation: [
          0, 0.878200162, 13.00147939, 49.08561756, 80.47857205, 97.20468254,
          103.1140505, 102.8393771, 99.39309958, 94.48294252, 89.03071172,
          79.12970742, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
        ]
      },
      {
        accuYield: 'RAD_UT_CF30',
        status: 'Completed',
        accuYieldAgeOfPlantation: [
          0, 0.966020178, 14.30162733, 53.99417932, 88.52642926, 106.9251508,
          113.4254555, 113.1233148, 109.3324095, 103.9312368, 97.93378289,
          87.04267816, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
        ]
      }
    ];
  }

  private _transformPeatDefaults(
    rows: PineEconomicAnalysisDefault[]
  ): PeatDefaultValuesModels {
    const result: PeatDefaultValuesModels = {};
    const keys = ['fpp', 'accu', 'ptr'];

    return rows.reduce<PeatDefaultValuesModels>((result, row) => {
      const sectionKey = this._toCamelCase(row.section);
      const costGroupKey = row.costGroup
        ? this._toCamelCase(row.costGroup)
        : null;

      const itemLower = row.item.toLowerCase();

      const itemKey = keys.includes(itemLower)
        ? itemLower
        : this._toCamelCase(row.item);

      const valueRange: ValueRange = {
        defaultValue: row.defaultValue,
        rangeLower: row.rangeLower,
        rangeUpper: row.rangeUpper
      };

      // Create the section before reading it.
      if (!result[sectionKey]) {
        result[sectionKey] = {};
      }

      const section = result[sectionKey] as Record<
        string,
        PeatDefaultValuesNode
      >;

      if (!costGroupKey) {
        section[itemKey] = valueRange;
        return result;
      }

      // Create the nested CostGroup if it does not exist.
      if (!section[costGroupKey]) {
        section[costGroupKey] = {};
      }

      const costGroup = section[costGroupKey] as {
        [key: string]: PeatDefaultValuesNode;
      };

      costGroup[itemKey] = valueRange;

      return result;
    }, {});
  }

  private _toCamelCase(value: string): string {
    if (!value) {
      return value;
    }

    return value.charAt(0).toLowerCase() + value.slice(1);
  }
}
